import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';
import { DEFAULT_THEME_COLOR } from 'appenv';
import styled from 'styled-components';
import { Trans, useTranslation } from 'react-i18next';
import moment from 'moment-timezone';
import { Link } from 'react-router-dom';
import Routes from 'Routes';
import { Webinar, WebinarType } from 'models/webinars';
import { selectLocale, selectMomentLocalizer } from 'models/localization';
import { useSelector } from 'react-redux';
import { BREAKPOINT } from 'pages/webinar/constants';
import ShowMore from 'components/utils/ShowMore';
import { isSupportedStream } from 'utils/streamableLinkUtils';
import { hexToRGB, rgbaToRGB } from 'utils/colorUtils';
import { LiveWebinarMultiTracksOptions } from 'pages/webinar/live/LiveWebinarListOptions';
import QuillRichtextDisplay from 'components/QuillRichtextDisplay';
import WebinarRootContainer from '../../components/WebinarRootContainer';
import WatchNowButton from '../../components/WatchNowButton';
import ComingSoonButton from '../../components/ComingSoonButton';
import SessionEndedButton from '../../components/SessionEndedButton';
import SpeakerList from '../../SpeakerList';

const HeadRowContainer = styled.div<{ $isMultiTrack: boolean }>`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  ${({ $isMultiTrack }) => $isMultiTrack && `
    flex-direction: column;

    .live-tag {
      margin: 8px 0;
    }
  `}
  width: ${({ $isMultiTrack }) => ($isMultiTrack ? '100%' : 'fit-content')};

  @media (max-width: ${BREAKPOINT}px) {
    flex-direction: column;
  }
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;

  & > * {
    width: 100%;
  }
`;

const DateTimeZoneContainer = styled.div<{ isMultiTrackSession: boolean }>`
  font-style: normal;
  font-weight: bold;
  display: flex;
  flex-wrap: wrap;
  font-size: 14px;
  line-height: 20px;
`;

const DateContainer = styled.div`
  margin-right: 4px;
`;
const TimeZoneContainer = styled.div`
  color: #999;
`;

const LiveTag = styled.div`
  background: #F77;
  border-radius: 2px;
  padding: 3.5px 10px;
  margin-left: 16px;
  font-weight: bold;
  font-size: 14px;
  line-height: 17px;
  color: #FFF;
  text-transform: uppercase;

  @media (max-width: ${BREAKPOINT}px) {
    margin: 8px 0;
  }
`;

LiveTag.defaultProps = {
  className: 'live-tag',
};

const NameContainer = styled(Link)`
  display: inline-block;
  font-style: normal;
  font-weight: bold;
  color: ${({ color }) => color};
  text-decoration: none;
  height: unset;
  word-break: break-word;

  @media (min-width: ${BREAKPOINT}px) {
    font-size: 24px;
    line-height: 29px;
  }

  @media (max-width: ${BREAKPOINT}px) {
    font-size: 18px;
    line-height: 22px;
  }
`;

const UnsupportedWebinarNameContainer = styled.a`
  display: inline-block;
  font-style: normal;
  font-weight: bold;
  color: ${({ color }) => color};
  text-decoration: none;
  height: unset;
  word-break: break-word;

  @media (min-width: ${BREAKPOINT}px) {
    font-size: 24px;
    line-height: 29px;
  }

  @media (max-width: ${BREAKPOINT}px) {
    font-size: 18px;
    line-height: 22px;
  }
`;

const WebinarDescriptionContainer = styled.div<any>`
  display: inline-block;
  font-size: 16px;
  line-height: 22px;
  margin: 16px 0 0 0;
  height: ${({ isMultiTrackSession }) => (isMultiTrackSession ? '100%' : 'unset')};
  max-width: 100%;

  & > *:first-child {
    margin-top: 0;
  }

  & > * {
    word-break: break-word;
  }
`;
interface LiveWebinarItemProps {
  webinar: Webinar;
  multiTrackOptions: LiveWebinarMultiTracksOptions;
  groupId: string;
}

enum LiveWebinarState {
  ComingSoon, Ended, Live, Replay
}

const TopTitleContainer = styled.div`
  display: flex;
  justify-content: space-between;
`;

const TopRightActionButtonWrapper = styled.div`
  width: 182px;
  flex: 0 0 auto;

  @media (max-width: ${BREAKPOINT}px) {
    display: none;
  }
`;

const BottomActionButtonWrapper = styled.div<{ isMultiTrackSession: boolean }>`
  margin-top: 16px;
  display: ${({ isMultiTrackSession }) => (isMultiTrackSession ? 'block' : 'none')};

  @media (max-width: ${BREAKPOINT}px) {
    display: block;
  }
`;

const TimeDisplayComponent = ({ webinar: { startAt, endAt }, isMultiTrackSession }: { webinar: Webinar; isMultiTrackSession: boolean }) => {
  const durationInMins = Math.abs(moment.duration(moment(startAt).diff(endAt)).asMinutes());
  const momentLocalizer = useSelector(selectMomentLocalizer);
  const startTime = momentLocalizer(startAt).format('h:mmA');
  const endTime = momentLocalizer(endAt).format('h:mmA');
  const timezoneString = `GMT${momentLocalizer(startAt).format('Z')}`;
  return (
    <DateTimeZoneContainer isMultiTrackSession={isMultiTrackSession}>
      <Trans
        defaults={`${startTime} - ${endTime} (${durationInMins} mins) (${timezoneString})`}
        i18nKey="webinar.time_format"
        components={{ b: <DateContainer />, i: <TimeZoneContainer /> }}
        values={{
          startTime, endTime, duration: durationInMins, timezone: timezoneString,
        }}
      />
    </DateTimeZoneContainer>
  );
};

const LiveWebinarItem = ({
  webinar, webinar: { startAt, endAt }, multiTrackOptions, groupId = '',
}: LiveWebinarItemProps) => {
  const { t } = useTranslation();
  const webinarTrackId = webinar.track?.id;
  const isMultiTrackSession = useMemo(() => multiTrackOptions.multiTracksId.indexOf(webinarTrackId) !== -1, [multiTrackOptions.multiTracksId, webinarTrackId]);
  const locale: string = useSelector(selectLocale);
  const { themeColor } = useSelector((s: any) => s.eventConfigs);
  const translation = webinar.translations[locale] ?? ({} as any);

  const [webinarState, setWebinarState] = useState(LiveWebinarState.ComingSoon);
  const supportedStream = useMemo(() => isSupportedStream(translation.url), [translation.url]);

  const webinarStateButtonsMap = {
    [LiveWebinarState.ComingSoon]: <ComingSoonButton />,
    [LiveWebinarState.Live]: <WatchNowButton kind={webinar.kind} id={webinar.id} name={translation.title} url={supportedStream ? Routes.webinarSession(webinar.id) : translation.url} supportedStream={supportedStream} />,
    [LiveWebinarState.Ended]: <SessionEndedButton />,
    [LiveWebinarState.Replay]: <WatchNowButton kind={WebinarType.OnDemand} id={webinar.id} name={translation.title} url={Routes.webinarSession(webinar.id)} supportedStream={supportedStream} />,
  };

  const showMoreRGB = useMemo(() => {
    const trackHex = webinar.track?.color;
    if (!trackHex) return undefined;
    const defaultAlpha = 0.2;
    const RGB = hexToRGB(trackHex);
    return rgbaToRGB([...RGB, defaultAlpha]);
  }, []);

  useEffect(() => {
    const update = () => {
      const currentTime = moment();
      if (currentTime.isBefore(startAt)) setWebinarState(LiveWebinarState.ComingSoon);
      else if (!currentTime.isAfter(endAt)) setWebinarState(LiveWebinarState.Live);
      else setWebinarState(LiveWebinarState.Ended);
    };
    update();
    const taskId = setInterval(() => update(), 1000);
    return () => clearInterval(taskId);
  }, [startAt, endAt]);

  return (
    <WebinarRootContainer
      isMultiTrackSession={isMultiTrackSession}
      trackColor={isMultiTrackSession ? webinar.track?.color : null}
    >
      <ContentContainer>
        <div>
          <TopTitleContainer className={groupId}>
            <div>
              <HeadRowContainer $isMultiTrack={isMultiTrackSession}>
                <TimeDisplayComponent webinar={webinar} isMultiTrackSession={isMultiTrackSession} />
                {webinarState === LiveWebinarState.Live && <LiveTag>{t('webinar.live', 'Live')}</LiveTag>}
              </HeadRowContainer>
              {isSupportedStream(translation.url) ? (
                <NameContainer
                  color={themeColor || DEFAULT_THEME_COLOR}
                  to={Routes.webinarSession(webinar.id)}
                >
                  {translation.title}
                </NameContainer>
              ) : (
                <UnsupportedWebinarNameContainer
                  target="_blank"
                  rel="noopener noreferrer"
                  href={translation?.url}
                  color={themeColor || DEFAULT_THEME_COLOR}
                >
                  {translation.title}
                </UnsupportedWebinarNameContainer>
              )}
            </div>
            {
              // eslint-disable-next-line
              !isMultiTrackSession && (translation.url?.trim()?.length > 0 || translation?.cloudflare_stream_id?.trim().length > 0) && (
                <TopRightActionButtonWrapper>
                  {webinarStateButtonsMap[webinarState]}
                </TopRightActionButtonWrapper>
              )
            }
          </TopTitleContainer>
          {
            translation.description && translation.description.trim().length > 0 && (
              <WebinarDescriptionContainer isMultiTrackSession={isMultiTrackSession}>
                <ShowMore maxHeight={100} backgroundRGB={showMoreRGB}>
                  <QuillRichtextDisplay html={translation?.description} />
                </ShowMore>
              </WebinarDescriptionContainer>
            )
          }
        </div>
        <div>
          <SpeakerList
            webinarId={webinar.id}
            speakers={webinar.speakers}
            backgroundRGB={showMoreRGB}
          />
          {
            // eslint-disable-next-line
            (translation?.url?.trim().length > 0 || translation?.cloudflare_stream_id?.trim().length > 0) && (
              <BottomActionButtonWrapper isMultiTrackSession={isMultiTrackSession}>
                {webinarStateButtonsMap[webinarState]}
              </BottomActionButtonWrapper>
            )
          }
        </div>
      </ContentContainer>
    </WebinarRootContainer>
  );
};

export default LiveWebinarItem;
